import { PDFDocument, StandardFonts, rgb, type PDFFont } from 'pdf-lib';

export type ContractPdfFontFamily = 'helvetica' | 'times' | 'courier';

export type ContractPdfBox = { x: number; y: number; width: number; height: number };

/** One editable block. Coordinates are PDF points, origin bottom-left. `baseline` is the first line's baseline. */
export type ContractTextRun = {
  id: string;
  pageIndex: number;
  x: number;
  baseline: number;
  text: string;
  fontSize: number;
  bold: boolean;
  font: ContractPdfFontFamily;
  /** Wrap width in PDF points. Text past this width continues on the next line. */
  maxWidth: number;
  /** Distance between baselines, in PDF points. */
  lineHeight: number;
  /** Original glyphs to hide when this block changes or is deleted. Null for text added in the editor. */
  erase: ContractPdfBox | null;
  origin: {
    x: number;
    baseline: number;
    text: string;
    fontSize: number;
    bold: boolean;
    font: ContractPdfFontFamily;
  } | null;
};

export type ContractSignatureLine = {
  id: string;
  pageIndex: number;
  x: number;
  y: number;
  width: number;
};

export type ContractPdfEditModel = {
  runs: ContractTextRun[];
  signatures: ContractSignatureLine[];
  /** Original glyphs whose line was removed. */
  erasures: Array<ContractPdfBox & { pageIndex: number }>;
};

export type ExtractedTextPiece = {
  str: string;
  x: number;
  baseline: number;
  width: number;
  fontSize: number;
  bold: boolean;
  font: ContractPdfFontFamily;
};

/** A straight horizontal or vertical stroke printed on the page, such as an underline or a table border. */
export type ContractPdfRule = {
  pageIndex: number;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  width: number;
  /** Stroke color, each channel 0 to 1. */
  color: [number, number, number];
};

/**
 * The part of `rule` that painting `box` white would erase, or null when the
 * box misses it. `minWidth` covers renderers that draw thin lines wider.
 */
export function ruleInsideBox(
  rule: ContractPdfRule,
  box: ContractPdfBox,
  minWidth = 0
): { x1: number; y1: number; x2: number; y2: number } | null {
  const half = Math.max(rule.width, minWidth) / 2;
  const top = box.y + box.height;
  const right = box.x + box.width;
  if (Math.abs(rule.y2 - rule.y1) <= Math.abs(rule.x2 - rule.x1)) {
    const y = (rule.y1 + rule.y2) / 2;
    if (y + half < box.y || y - half > top) return null;
    const from = Math.max(Math.min(rule.x1, rule.x2), box.x);
    const to = Math.min(Math.max(rule.x1, rule.x2), right);
    return to - from > 0.05 ? { x1: from, y1: y, x2: to, y2: y } : null;
  }
  const x = (rule.x1 + rule.x2) / 2;
  if (x + half < box.x || x - half > right) return null;
  const from = Math.max(Math.min(rule.y1, rule.y2), box.y);
  const to = Math.min(Math.max(rule.y1, rule.y2), top);
  return to - from > 0.05 ? { x1: x, y1: from, x2: x, y2: to } : null;
}

const PUNCTUATION: Record<string, string> = {
  '\u2018': "'",
  '\u2019': "'",
  '\u201c': '"',
  '\u201d': '"',
  '\u2013': '-',
  '\u2014': '-',
  '\u2011': '-',
  '\u2026': '...',
  '\u00a0': ' ',
  '\u2022': '*',
};

export function contractFontFamily(name: string): ContractPdfFontFamily {
  const blob = name.toLowerCase();
  if (blob.includes('courier') || blob.includes('mono')) return 'courier';
  if (blob.includes('times') || blob.includes('roman') || (blob.includes('serif') && !blob.includes('sans'))) {
    return 'times';
  }
  return 'helvetica';
}

export function contractFontIsBold(name: string, boldFlag?: boolean): boolean {
  if (boldFlag) return true;
  return /bold|black|semibold|demi/i.test(name);
}

export function contractTextRunChanged(run: ContractTextRun): boolean {
  if (!run.text.trim()) return Boolean(run.erase);
  if (!run.origin) return true;
  if (run.text !== run.origin.text) return true;
  if (Math.abs(run.x - run.origin.x) > 0.5) return true;
  if (Math.abs(run.baseline - run.origin.baseline) > 0.5) return true;
  if (Math.abs(run.fontSize - run.origin.fontSize) > 0.15) return true;
  if (run.bold !== run.origin.bold) return true;
  if (run.font !== run.origin.font) return true;
  return false;
}

/**
 * Join words on the same baseline into lines. A gap wider than about two
 * spaces starts a new block, so columns and values printed at a fixed spot
 * (a quantity on its blank, a name after its label) keep their position.
 */
export function textPiecesToRuns(
  pageIndex: number,
  pieces: ExtractedTextPiece[],
  nextId: () => string
): ContractTextRun[] {
  const usable = pieces
    .filter((piece) => piece.str.trim().length > 0 && piece.fontSize >= 3)
    .map((piece) => ({ ...piece, str: piece.str.replace(/\s+/g, ' ').trim() }))
    .sort((a, b) => b.baseline - a.baseline || a.x - b.x);

  const groups: ExtractedTextPiece[][] = [];
  for (const piece of usable) {
    const group = groups[groups.length - 1];
    const prev = group?.[group.length - 1];
    const gap = prev ? piece.x - (prev.x + prev.width) : Infinity;
    const sameLine =
      prev &&
      Math.abs(prev.baseline - piece.baseline) <= 1.25 &&
      Math.abs(prev.fontSize - piece.fontSize) <= 0.6 &&
      prev.bold === piece.bold &&
      prev.font === piece.font &&
      gap <= prev.fontSize * 0.6 &&
      gap > -prev.fontSize;
    if (sameLine && group) group.push(piece);
    else groups.push([piece]);
  }

  return groups.map((group) => {
    group.sort((a, b) => a.x - b.x);
    let text = '';
    let cursor = group[0].x;
    for (const part of group) {
      const gap = part.x - cursor;
      if (text && gap > part.fontSize * 0.12 && !text.endsWith(' ')) text += ' ';
      text += part.str;
      cursor = part.x + part.width;
    }
    const fontSize = group[0].fontSize;
    const baseline = group[0].baseline;
    const minX = Math.min(...group.map((part) => part.x));
    const maxX = Math.max(...group.map((part) => part.x + part.width));
    const descent = fontSize * 0.22;
    const ascent = fontSize * 0.8;
    const padX = 0.7;
    const padY = 0.35;
    const run: ContractTextRun = {
      id: nextId(),
      pageIndex,
      x: minX,
      baseline,
      text,
      fontSize,
      bold: group[0].bold,
      font: group[0].font,
      maxWidth: 0,
      lineHeight: fontSize * 1.2,
      erase: {
        x: minX - padX,
        y: baseline - descent - padY,
        width: Math.max(1, maxX - minX) + padX * 2,
        height: ascent + descent + padY * 2,
      },
      origin: null,
    };
    run.origin = {
      x: run.x,
      baseline: run.baseline,
      text: run.text,
      fontSize: run.fontSize,
      bold: run.bold,
      font: run.font,
    };
    return run;
  });
}

const RIGHT_MARGIN = 36;

function unionErase(runs: ContractTextRun[]): ContractPdfBox | null {
  const boxes = runs.flatMap((run) => (run.erase ? [run.erase] : []));
  if (boxes.length === 0) return null;
  const x = Math.min(...boxes.map((box) => box.x));
  const y = Math.min(...boxes.map((box) => box.y));
  const right = Math.max(...boxes.map((box) => box.x + box.width));
  const top = Math.max(...boxes.map((box) => box.y + box.height));
  return { x, y, width: right - x, height: top - y };
}

function joinParagraphLines(lines: string[]): string {
  let text = '';
  for (const line of lines) {
    if (!text) {
      text = line;
      continue;
    }
    if (text.endsWith('-') && /^[A-Za-z]/.test(line)) text += line;
    else text += ` ${line}`;
  }
  return text;
}

function canJoinParagraph(prev: ContractTextRun, next: ContractTextRun): boolean {
  if (prev.pageIndex !== next.pageIndex) return false;
  if (prev.bold !== next.bold || prev.font !== next.font) return false;
  if (Math.abs(prev.fontSize - next.fontSize) > 0.6) return false;
  if (Math.abs(prev.x - next.x) > 4) return false;
  const gap = prev.baseline - next.baseline;
  return gap >= prev.fontSize * 0.9 && gap <= prev.fontSize * 1.6;
}

function lineRight(run: ContractTextRun): number {
  if (run.erase) return run.erase.x + run.erase.width;
  return run.x + Math.max(8, run.text.length * run.fontSize * 0.45);
}

/** True when another run sits on this same row, to the right. Those rows are columns, not paragraph lines. */
function hasTextBeside(run: ContractTextRun, runs: ContractTextRun[]): boolean {
  const edge = lineRight(run);
  return runs.some((other) => {
    if (other.id === run.id || other.pageIndex !== run.pageIndex) return false;
    if (Math.abs(other.baseline - run.baseline) > Math.max(1.5, run.fontSize * 0.35)) return false;
    return other.x >= edge - 1;
  });
}

/** Join stacked body lines into paragraphs so typing reflows instead of running off the page. */
export function mergeContractParagraphs(runs: ContractTextRun[]): ContractTextRun[] {
  const sorted = [...runs].sort((a, b) => a.pageIndex - b.pageIndex || b.baseline - a.baseline || a.x - b.x);
  const groups: ContractTextRun[][] = [];
  for (const run of sorted) {
    const group = groups[groups.length - 1];
    const prev = group?.[group.length - 1];
    const beside = hasTextBeside(run, sorted) || (prev ? hasTextBeside(prev, sorted) : false);
    if (prev && canJoinParagraph(prev, run) && !beside) group.push(run);
    else groups.push([run]);
  }
  return groups.map((group) => {
    if (group.length === 1) return group[0];
    const gaps = group.slice(1).map((run, index) => group[index].baseline - run.baseline);
    const lineHeight = gaps.reduce((sum, gap) => sum + gap, 0) / gaps.length;
    const text = joinParagraphLines(group.map((run) => run.text));
    const first = group[0];
    const merged: ContractTextRun = {
      ...first,
      text,
      lineHeight,
      erase: unionErase(group),
      origin: null,
    };
    merged.origin = {
      x: merged.x,
      baseline: merged.baseline,
      text: merged.text,
      fontSize: merged.fontSize,
      bold: merged.bold,
      font: merged.font,
    };
    return merged;
  });
}

/** Set each block's wrap width to the page margin, or to the next column when one sits beside it. */
export function assignContractWrapWidths(runs: ContractTextRun[], pageWidths: number[]): ContractTextRun[] {
  return runs.map((run) => {
    const pageWidth = pageWidths[run.pageIndex] || 612;
    let maxWidth = Math.max(72, pageWidth - run.x - RIGHT_MARGIN);
    const top = run.baseline + run.fontSize * 0.85;
    const bottom = run.erase ? run.erase.y : run.baseline - run.fontSize * 0.3;
    const blockHeight = Math.max(run.fontSize, top - bottom);
    for (const other of runs) {
      if (other.id === run.id || other.pageIndex !== run.pageIndex || other.x < run.x + 12) continue;
      const otherTop = other.baseline + other.fontSize * 0.85;
      const otherBottom = other.erase ? other.erase.y : other.baseline - other.fontSize * 0.3;
      const overlap = Math.min(top, otherTop) - Math.max(bottom, otherBottom);
      if (overlap < run.fontSize * 0.45) continue;
      // A short label beside the last line must not squeeze the whole paragraph into that column.
      if (blockHeight > run.fontSize * 1.8 && overlap < blockHeight * 0.55) continue;
      maxWidth = Math.min(maxWidth, Math.max(72, other.x - run.x - 8));
    }
    // A line that already fits must stay on one row. Wrapping starts only after more text is added.
    const inkWidth = Math.max(8, lineRight(run) - run.x);
    return { ...run, maxWidth: Math.max(maxWidth, inkWidth) };
  });
}

function isAuthorizationClientPlaceholder(text: string): boolean {
  const normalized = text
    .replace(/[\u201c\u201d]/g, '"')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
  return normalized === 'business name "client"';
}

function runsShareCell(a: ContractTextRun, b: ContractTextRun): boolean {
  if (a.pageIndex !== b.pageIndex) return false;
  if (Math.abs(a.baseline - b.baseline) > Math.max(1.5, a.fontSize * 0.35)) return false;
  const aRight = lineRight(a);
  const bRight = lineRight(b);
  return a.x < bRight - 1 && b.x < aRight - 1;
}

/**
 * The company name is drawn on top of the printed Authorization label.
 * Drop that label so the editor shows the company name in its place, and
 * hide the buried glyphs with the company name's erase box.
 */
export function dropCoveredClientHeading(runs: ContractTextRun[]): ContractTextRun[] {
  const dropped = new Set<string>();
  runs.forEach((run) => {
    if (!isAuthorizationClientPlaceholder(run.text) || dropped.has(run.id)) return;
    const cover = runs.find(
      (other) =>
        other.id !== run.id &&
        !dropped.has(other.id) &&
        !isAuthorizationClientPlaceholder(other.text) &&
        runsShareCell(run, other)
    );
    if (!cover) return;
    dropped.add(run.id);
    const box = unionErase([cover, run]);
    if (box) cover.erase = box;
  });
  return runs.filter((run) => !dropped.has(run.id));
}

export function prepareContractRuns(runs: ContractTextRun[], pageWidths: number[]): ContractTextRun[] {
  return assignContractWrapWidths(mergeContractParagraphs(dropCoveredClientHeading(runs)), pageWidths);
}

/** Wrap paragraphs onto as many rows as the width allows. A word longer than the row is split. */
export function wrapContractText(text: string, maxWidth: number, widthOf: (sample: string) => number): string[] {
  const width = Math.max(24, maxWidth);
  const paragraphs = text.replace(/\r/g, '').split('\n');
  const lines: string[] = [];
  const pushWord = (current: string, word: string): string => {
    if (widthOf(word) > width) {
      if (current) lines.push(current);
      let chunk = '';
      for (const ch of word) {
        const next = chunk + ch;
        if (chunk && widthOf(next) > width) {
          lines.push(chunk);
          chunk = ch;
        } else chunk = next;
      }
      return chunk;
    }
    const candidate = current ? `${current} ${word}` : word;
    if (current && widthOf(candidate) > width) {
      lines.push(current);
      return word;
    }
    return candidate;
  };
  paragraphs.forEach((paragraph, index) => {
    const words = paragraph.split(/\s+/).filter(Boolean);
    if (words.length === 0) {
      if (index > 0) lines.push('');
      return;
    }
    let current = '';
    for (const word of words) current = pushWord(current, word);
    if (current) lines.push(current);
  });
  return lines.length > 0 ? lines : [''];
}

export type ContractTextPlacement = { id: string; baseline: number; shift: number };

/**
 * Move later blocks up or down when a block above them grows or shrinks,
 * so wrapped rows do not land on top of the next paragraph.
 */
export function layoutContractBaselines(
  runs: ContractTextRun[],
  widthOf: (run: ContractTextRun, sample: string) => number
): ContractTextPlacement[] {
  const byPage = new Map<number, ContractTextRun[]>();
  for (const run of runs) {
    const list = byPage.get(run.pageIndex) ?? [];
    list.push(run);
    byPage.set(run.pageIndex, list);
  }
  const placements: ContractTextPlacement[] = [];
  byPage.forEach((pageRuns) => {
    const sorted = [...pageRuns].sort((a, b) => b.baseline - a.baseline || a.x - b.x);
    const placed: Array<{ x: number; maxWidth: number; shift: number; delta: number }> = [];
    for (const run of sorted) {
      const maxWidth = run.maxWidth > 20 ? run.maxWidth : 468;
      const leading = run.lineHeight > 0 ? run.lineHeight : run.fontSize * 1.2;
      const count = (value: string) => Math.max(1, wrapContractText(value, maxWidth, (sample) => widthOf(run, sample)).length);
      const currentLines = count(run.text || '');
      const originLines = run.origin ? count(run.origin.text) : 1;
      let delta = (currentLines - originLines) * leading;
      if (Math.abs(delta) < 0.75) delta = 0;
      let shift = 0;
      let found = false;
      for (const prev of placed) {
        const overlaps = run.x < prev.x + prev.maxWidth - 4 && prev.x < run.x + maxWidth - 4;
        if (!overlaps) continue;
        const effect = prev.shift + prev.delta;
        if (!found || effect > shift) {
          shift = effect;
          found = true;
        }
      }
      placed.push({ x: run.x, maxWidth, shift, delta });
      placements.push({ id: run.id, baseline: run.baseline - shift, shift });
    }
  });
  return placements;
}

function drawableText(text: string, font: PDFFont): string {
  let out = '';
  for (const ch of text.replace(/\r/g, '')) {
    if (ch === '\n') continue;
    const mapped = PUNCTUATION[ch] ?? ch;
    try {
      font.encodeText(mapped);
      out += mapped;
    } catch {
      out += ' ';
    }
  }
  return out;
}

function fontFor(
  fonts: Record<string, PDFFont>,
  family: ContractPdfFontFamily,
  bold: boolean
): PDFFont {
  if (family === 'times') return bold ? fonts.timesBold : fonts.times;
  if (family === 'courier') return bold ? fonts.courierBold : fonts.courier;
  return bold ? fonts.helveticaBold : fonts.helvetica;
}

function paintWhite(
  page: ReturnType<PDFDocument['getPages']>[number] | undefined,
  box: ContractPdfBox
) {
  if (!page || box.width <= 0 || box.height <= 0) return;
  page.drawRectangle({
    x: box.x,
    y: box.y,
    width: box.width,
    height: box.height,
    color: rgb(1, 1, 1),
    borderWidth: 0,
  });
}

/** A white-out also cuts the underlines and borders under the text. Draw those pieces back. */
function restoreRules(
  page: ReturnType<PDFDocument['getPages']>[number] | undefined,
  box: ContractPdfBox & { pageIndex: number },
  rules: ContractPdfRule[]
) {
  if (!page) return;
  for (const rule of rules) {
    if (rule.pageIndex !== box.pageIndex) continue;
    const piece = ruleInsideBox(rule, box);
    if (!piece) continue;
    page.drawLine({
      start: { x: piece.x1, y: piece.y1 },
      end: { x: piece.x2, y: piece.y2 },
      thickness: rule.width,
      color: rgb(rule.color[0], rule.color[1], rule.color[2]),
    });
  }
}

/**
 * Apply edits onto a pristine contract PDF. Lines the user did not change stay as
 * originally drawn. Changed and new lines are written in a standard font.
 * `rules` are the page's printed strokes, kept intact under erased text.
 */
export async function applyContractPdfEdits(
  pdfBytes: Uint8Array,
  model: ContractPdfEditModel,
  rules: ContractPdfRule[] = []
): Promise<Uint8Array> {
  const doc = await PDFDocument.load(pdfBytes);
  const fonts = {
    helvetica: await doc.embedFont(StandardFonts.Helvetica),
    helveticaBold: await doc.embedFont(StandardFonts.HelveticaBold),
    times: await doc.embedFont(StandardFonts.TimesRoman),
    timesBold: await doc.embedFont(StandardFonts.TimesRomanBold),
    courier: await doc.embedFont(StandardFonts.Courier),
    courierBold: await doc.embedFont(StandardFonts.CourierBold),
  };
  const pages = doc.getPages();

  const whiteouts = [
    ...model.erasures,
    ...model.runs.flatMap((run) =>
      run.erase && contractTextRunChanged(run) ? [{ pageIndex: run.pageIndex, ...run.erase }] : []
    ),
  ];
  for (const box of whiteouts) paintWhite(pages[box.pageIndex], box);
  for (const box of whiteouts) restoreRules(pages[box.pageIndex], box, rules);

  for (const run of model.runs) {
    const text = run.text.trim();
    if (!text || !contractTextRunChanged(run)) continue;
    const page = pages[run.pageIndex];
    if (!page) continue;
    const font = fontFor(fonts, run.font, run.bold);
    const maxWidth = run.maxWidth > 20 ? run.maxWidth : Math.max(72, page.getWidth() - run.x - 36);
    const leading = run.lineHeight > 0 ? run.lineHeight : run.fontSize * 1.2;
    const lines = wrapContractText(text, maxWidth, (sample) => {
      try {
        return font.widthOfTextAtSize(sample, run.fontSize);
      } catch {
        return font.widthOfTextAtSize(drawableText(sample, font), run.fontSize);
      }
    });
    lines.forEach((line, index) => {
      const drawn = drawableText(line, font).trim();
      if (!drawn) return;
      page.drawText(drawn, {
        x: run.x,
        y: run.baseline - index * leading,
        size: run.fontSize,
        font,
        color: rgb(0, 0, 0),
      });
    });
  }

  const labelFont = fonts.helvetica;
  for (const line of model.signatures) {
    const page = pages[line.pageIndex];
    if (!page) continue;
    const width = Math.max(36, line.width);
    page.drawLine({
      start: { x: line.x, y: line.y },
      end: { x: line.x + width, y: line.y },
      thickness: 0.8,
      color: rgb(0, 0, 0),
    });
    page.drawText('Signature', {
      x: line.x,
      y: line.y + 3,
      size: 8,
      font: labelFont,
      color: rgb(0.15, 0.15, 0.15),
    });
  }

  return doc.save();
}
